import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { WipeLink } from '../components/Wipe';
import { MaskedLines, FadeUp, Counter, Eyebrow } from '../components/Reveal';
import Marquee from '../components/Marquee';
import Canopy from '../components/Canopy';
import Guestbook from '../components/Guestbook';
import ChapterIndex from '../components/ChapterIndex';
import Footer from '../components/Footer';

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const motifY = useTransform(scrollYProgress, [0, 1], ['0%', '45%']);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col justify-between overflow-hidden px-5 md:px-10 lg:px-24 pt-44 md:pt-52" data-testid="hero-section">
        <motion.div
          initial={{ y: '-100%' }}
          animate={{ y: 0 }}
          transition={{ duration: 1.1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="absolute top-14 md:top-16 left-0 right-0 z-20"
        >
          <Canopy className="h-[58px] md:h-[76px] w-full opacity-95" />
        </motion.div>
        <motion.div style={{ y: motifY }} className="absolute -right-32 md:-right-16 top-[46vh] md:top-[36vh] w-[70vw] md:w-[36vw] opacity-90 pointer-events-none select-none">
          <motion.img
            src="/assets/motifs/Group-30.svg"
            alt=""
            className="w-full animate-spin-slow"
            initial={{ opacity: 0, scale: 0.8, rotate: -20 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.6, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          />
        </motion.div>

        <div className="relative flex items-start justify-between">
          <FadeUp delay={0.3} y={12}>
            <p className="text-[11px] md:text-xs font-semibold uppercase tracking-[0.35em] text-support max-w-[300px] leading-relaxed">
              Graduation Project, 2026<br />Unitedworld Institute of Design
            </p>
          </FadeUp>
          <FadeUp delay={0.4} y={12} className="hidden md:block text-right">
            <p className="text-[11px] md:text-xs font-semibold uppercase tracking-[0.35em] text-support leading-relaxed">
              A Systemic Proposal for<br />Clarity · Control · Growth
            </p>
          </FadeUp>
        </div>

        <div className="relative z-10 mt-10 md:mt-14">
          <MaskedLines
            onLoad
            delay={0.35}
            lines={['Designing for the', 'Next Phase of']}
            className="text-[11.5vw] md:text-[7.2vw] font-light tracking-tighter leading-[0.95] text-ink"
          />
          <MaskedLines
            onLoad
            delay={0.6}
            lines={["India's Offline", 'Retail']}
            className="text-[11.5vw] md:text-[7.2vw] font-light tracking-tighter leading-[0.95]"
            lineClassName="text-brand-red"
          />
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 flex flex-col md:flex-row md:items-end gap-6 md:gap-16"
          >
            <p className="text-base md:text-lg text-ink/70 max-w-md leading-relaxed">
              A product service system for the kirana stores that carry the weight of
              everyday retail, written as a book, rebuilt as an experience.
            </p>
            <div className="flex items-center gap-4">
              <span className="h-px w-14 bg-ink/30" />
              <p className="text-xs md:text-sm font-medium uppercase tracking-[0.25em]">
                Aditya Ranka, Industrial Designer
              </p>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="relative mt-14 mb-8 flex items-center gap-4 text-support"
        >
          <span className="text-[11px] uppercase tracking-[0.35em]">Scroll to open the book</span>
          <motion.span
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: 'easeInOut' }}
            className="text-lg"
          >
            ↓
          </motion.span>
        </motion.div>
      </section>

      <Marquee
        items={["India's Offline Retail", 'Family-Run Stores', 'General Trade', 'Quick Commerce Pressure', 'System Thinking', 'Service Design']}
        className="border-y border-ink/10 py-5 text-xl md:text-3xl font-light uppercase tracking-tight text-ink/70"
      />

      {/* DEDICATION */}
      <section className="bg-brand-black text-paper px-5 md:px-10 lg:px-24 py-28 md:py-40" data-testid="dedication-section">
        <div className="max-w-4xl">
          <Eyebrow color="#F9526B">Dedication</Eyebrow>
          <MaskedLines
            lines={['To all the kirana store owners,', 'who carry the weight of everyday retail', 'with quiet strength,']}
            className="mt-10 text-3xl md:text-5xl font-light tracking-tight leading-[1.08]"
          />
          <FadeUp delay={0.3}>
            <p className="mt-10 text-paper/60 text-base md:text-lg leading-relaxed max-w-2xl">
              This is dedicated to you, for showing up every day and sustaining the backbone of
              Indian retail. Grateful for all that you have built and continue to carry.
              May you be blessed with strength and a long, enduring journey.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* PREMISE */}
      <section className="px-5 md:px-10 lg:px-24 py-28 md:py-40" data-testid="premise-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <Eyebrow color="#B50A00">The Premise</Eyebrow>
            <MaskedLines
              lines={['This time, it is not the', 'fear of finance,', 'but of irrelevance.']}
              className="mt-10 text-4xl md:text-6xl font-light tracking-tighter leading-[1.02] text-ink"
            />
          </div>
          <FadeUp delay={0.15} className="md:col-span-5 flex flex-col justify-end">
            <p className="text-base md:text-lg text-ink/70 leading-relaxed">
              India has always been a land of trading, and at its core lie offline channels of
              business, led by kirana shops, an ecosystem so widespread and unique to India.
              But the nature of competition has changed. Where competition used to be another
              human mind, this time it is data backed, technologically advanced, systemized
              and automated.
            </p>
            <p className="mt-5 text-base md:text-lg text-ink/70 leading-relaxed">
              This project is an attempt to understand that shift, and respond to it with a
              structured approach.
            </p>
          </FadeUp>
        </div>

        <div className="mt-20 md:mt-28 border-t border-ink/15 pt-14 md:pt-20">
          <Eyebrow color="#B50A00">Why This Matters</Eyebrow>
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <MaskedLines
              lines={['In the last two years,', 'the ground has shifted', 'beneath the corner shop.']}
              className="lg:col-span-6 text-3xl md:text-5xl font-light tracking-tight leading-[1.08] text-ink"
            />
            <FadeUp delay={0.15} className="lg:col-span-6 flex flex-col justify-end">
              <p className="text-base md:text-lg text-ink/70 leading-relaxed max-w-xl">
                Quick commerce has redrawn how India buys its daily needs, and the stores that
                once anchored every neighbourhood are quietly disappearing. The numbers below
                come from the research behind this book.
              </p>
            </FadeUp>
          </div>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 border-t border-ink/15">
          {[
            { v: 200000, suffix: '', label: 'kirana stores shut in two years', color: '#B50A00' },
            { v: 730, suffix: '', label: 'days, that is all it took', color: '#FF7E00' },
            { v: 1000000, suffix: '+', label: 'lives touched by the collapse', color: '#01114F' },
          ].map((s, i) => (
            <FadeUp key={i} delay={i * 0.08} className="border-b md:border-b-0 md:border-r last:border-r-0 border-ink/15 py-10 md:py-14 md:px-10 first:md:pl-0">
              <p className="text-4xl md:text-5xl font-light tracking-tight" style={{ color: s.color }} data-testid={`stat-${i}`}>
                <Counter to={s.v} />{s.suffix}
              </p>
              <p className="mt-3 text-sm uppercase tracking-[0.2em] text-support">{s.label}</p>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.2}>
          <p className="mt-6 text-[11px] uppercase tracking-[0.25em] text-support/70">
            Figures as documented in the book, from industry research and field observation
          </p>
        </FadeUp>
      </section>

      <ChapterIndex />

      {/* OUTCOME TEASER */}
      <section className="bg-brand-navy text-paper px-5 md:px-10 lg:px-24 py-28 md:py-36 overflow-hidden" data-testid="outcome-teaser">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Eyebrow color="#FFB000">The Outcome</Eyebrow>
            <MaskedLines
              lines={['A framework,', 'not a product.']}
              className="mt-8 text-5xl md:text-7xl font-light tracking-tight leading-[1.02]"
            />
            <FadeUp delay={0.2}>
              <p className="mt-8 text-paper/70 text-base md:text-lg leading-relaxed max-w-lg">
                The 3-Layer Approach, moving a store from solving for data, to solving for
                actionable clarity, to solving for expansion and growth, held together by the
                strategy diamond, system maps and touchpoints that keep every decision aligned.
              </p>
            </FadeUp>
            <FadeUp delay={0.3}>
              <WipeLink
                to="/chapter/resolution"
                data-testid="outcome-cta"
                className="group mt-10 inline-flex items-center gap-4 border border-paper/40 px-8 py-4 text-sm font-medium uppercase tracking-[0.25em] transition-colors duration-500 hover:bg-paper hover:text-brand-navy"
              >
                See the resolution
                <span className="transition-transform duration-500 group-hover:translate-x-2">→</span>
              </WipeLink>
              <WipeLink
                to="/saarthi"
                data-testid="case-study-teaser-link"
                className="group mt-6 block text-sm font-medium uppercase tracking-[0.25em] text-paper/60 transition-colors duration-300 hover:text-paper"
              >
                Then see it imagined, Retail Saarthi, a conceptual case study
                <span className="ml-2 inline-block transition-transform duration-500 group-hover:translate-x-2">→</span>
              </WipeLink>
            </FadeUp>
          </div>
          <FadeUp delay={0.15} className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -inset-8 bandhani-dot text-paper/10 pointer-events-none" />
              <motion.img
                src="/assets/book/framework-clean.png"
                alt="The 3-Layer Approach framework pyramid"
                data-testid="outcome-image"
                className="relative w-full aspect-[4/3] object-contain p-6 shadow-2xl bg-brand-navy"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.6 }}
              />
              <p className="relative mt-3 text-[11px] uppercase tracking-[0.25em] text-paper/40">
                The 3-Layer Approach, the outcome of the project
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="px-5 md:px-10 lg:px-24 py-28 md:py-40" data-testid="about-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Eyebrow color="#20431B">About the Author</Eyebrow>
            <MaskedLines
              lines={['Aditya', 'Ranka.']}
              className="mt-8 text-6xl md:text-8xl font-light tracking-tighter leading-[0.95] text-ink"
            />
            <FadeUp delay={0.2}>
              <p className="mt-6 text-xs uppercase tracking-[0.3em] text-support">
                Industrial Designer · Semester 8, UID Gandhinagar
              </p>
            </FadeUp>
          </div>
          <FadeUp delay={0.15} className="md:col-span-7 md:pt-24">
            <p className="text-lg md:text-2xl font-medium leading-relaxed text-ink/85">
              "Coming from a background of Marwaris and lalas, I understood the language of
              business long before the language of design. Now I use that language for
              designing."
            </p>
            <p className="mt-8 text-base md:text-lg text-ink/70 leading-relaxed max-w-2xl">
              A keen backer of design as a management tool, growing into a strategic system
              designer who runs his firm like a businessman, in the smartest and coolest ways.
              Guided on this project by Prof. Shhyam Prajapati, Dean of Industrial Design,
              Unitedworld Institute of Design.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="mailto:contact@adityarankaa.com" data-testid="about-mail-cta" className="border border-ink px-7 py-3.5 text-xs font-medium uppercase tracking-[0.25em] transition-colors duration-500 hover:bg-ink hover:text-paper">Mail</a>
              <a href="tel:+919826025454" data-testid="about-phone-cta" className="border border-ink px-7 py-3.5 text-xs font-medium uppercase tracking-[0.25em] transition-colors duration-500 hover:bg-ink hover:text-paper">[IN] +91 98260 25454</a>
              <a href="https://www.behance.net/adityarka" target="_blank" rel="noopener noreferrer" data-testid="about-behance-cta" className="border border-ink px-7 py-3.5 text-xs font-medium uppercase tracking-[0.25em] transition-colors duration-500 hover:bg-ink hover:text-paper">Behance</a>
              <a href="https://www.linkedin.com/in/adityarka" target="_blank" rel="noopener noreferrer" data-testid="about-linkedin-cta" className="border border-ink px-7 py-3.5 text-xs font-medium uppercase tracking-[0.25em] transition-colors duration-500 hover:bg-ink hover:text-paper">LinkedIn</a>
            </div>
            <WipeLink
              to="/about"
              data-testid="about-page-link"
              className="group mt-10 inline-flex items-center gap-3 text-sm font-medium uppercase tracking-[0.25em] text-ink"
            >
              <span className="border-b border-ink pb-1">The author's full story</span>
              <span className="transition-transform duration-500 group-hover:translate-x-2">→</span>
            </WipeLink>
          </FadeUp>
        </div>
      </section>

      <Guestbook />

      <Marquee
        fast
        items={['Genesis', 'Context', 'Discovery', 'Synthesize', 'Resolution']}
        separator="✳"
        className="border-t border-ink/10 py-6 text-4xl md:text-6xl font-light uppercase tracking-tighter text-ink/15"
      />

      <Footer />
    </div>
  );
};

export default Home;
